/**
 * Scan-related helper functions extracted from DashboardPage.js.
 *
 * Pure utilities (no React components): normalizers, validators, date/time pickers helpers,
 * identity helpers, timezone helpers, and validation logic for network/web scan inputs.
 */

export const numOr0 = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

export const clampProgressPercent = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.min(100, Math.max(0, n));
};

export const extractScanProgressPercent = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined || value === '') continue;

    if (typeof value === 'object') {
      const nested = extractScanProgressPercent(
        value.progressPercent,
        value.progress_percent,
        value.progress,
        value.percent,
        value.percentage,
        value.percent_complete,
        value.completion,
        value.value,
      );
      if (nested !== null) return nested;
      continue;
    }

    const asNumber = clampProgressPercent(value);
    if (asNumber !== null) return asNumber;

    const match = String(value).trim().match(/(-?\d+(?:\.\d+)?)\s*%/);
    if (match) {
      const fromPercent = clampProgressPercent(match[1]);
      if (fromPercent !== null) return fromPercent;
    }
  }
  return null;
};

/**
 * Maps API deployment fields → UI labels `On-Premise` | `Perimeter` (Type column).
 *
 * Primary backend field: **`scanner_deployment`** on `NetworkScanDb` / `NetworkScanDbSerializer`:
 * - `on_premise` → On-Premise (grey building icon)
 * - `perimeter_scanner` → Perimeter (blue cloud icon)
 *
 * Other keys are fallbacks for alternate APIs; see BACKEND_INTEGRATION.md §4. Unknown → On-Premise.
 */
export const normalizeNetworkType = (raw) => {
  if (raw?.perimeter_scanner === true) return 'Perimeter';
  if (raw?.perimeter_scanner === false && (raw?.scanner_deployment || raw?.networkType || raw?.deployment_type)) {
    const explicitFalse = String(raw.scanner_deployment || raw.networkType || raw.deployment_type || '')
      .toLowerCase();
    if (explicitFalse.includes('perimeter')) return 'Perimeter';
  }
  const source =
    raw.scanner_deployment ??
    raw.networkType ??
    raw.network_type ??
    raw.deployment_type ??
    raw.deploymentType ??
    '';
  const s = String(source || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/-/g, '_');
  if (!s) return '';
  if (s === 'on_premise' || s === 'onprem' || s === 'on_prem' || s.startsWith('on_prem')) {
    return 'On-Premise';
  }
  if (s === 'perimeter_scanner' || s === 'perimeter' || s.startsWith('perimeter')) {
    return 'Perimeter';
  }
  return '';
};

/** Split raw network history rows by deployment (same rules as `normalizeNetworkType`). */
export function partitionNetworkHistoryRows(rawRows) {
  const onPremiseRows = [];
  const perimeterRows = [];
  const unknownRows = [];
  for (const raw of rawRows || []) {
    const nt = normalizeNetworkType({
      ...raw,
      scanner_deployment: raw.scanner_deployment ?? raw.deployment_type ?? raw.deploymentType,
    });
    if (nt === 'Perimeter') perimeterRows.push(raw);
    else if (nt === 'On-Premise') onPremiseRows.push(raw);
    else unknownRows.push(raw);
  }
  return { onPremiseRows, perimeterRows, unknownRows };
}

export function inferScheduledNetworkDeployment(raw) {
  if (raw?.perimeter_scanner === true) return 'perimeter_scanner';
  const explicit = String(raw?.scanner_deployment || raw?.deployment_type || '').trim().toLowerCase();
  if (explicit.includes('perimeter')) return 'perimeter_scanner';
  if (explicit.includes('on_prem') || explicit.includes('on-prem') || explicit.includes('on premise')) {
    return 'on_premise';
  }
  return '';
}

/** Reject placeholder scan IDs that would collapse distinct one-off schedules. */
export function isUsableScanIdentity(value) {
  const s = String(value ?? '').trim();
  if (!s) return false;
  const lower = s.toLowerCase();
  return lower !== 'n/a' && lower !== 'na' && lower !== 'null' && lower !== 'undefined' && lower !== 'none';
}

export function mapScheduledNetworkRowsToHistoryLikeRows(scheduleRows) {
  return (Array.isArray(scheduleRows) ? scheduleRows : []).map((row) => {
    const siteObject = row?.site && typeof row.site === 'object' ? row.site : null;
    const organizationObject =
      (row?.organization && typeof row.organization === 'object' && row.organization) ||
      (siteObject?.organization && typeof siteObject.organization === 'object' && siteObject.organization) ||
      null;
    const siteName =
      row?.site_name ||
      row?.siteName ||
      siteObject?.site_name ||
      siteObject?.name ||
      '';
    const organizationName =
      row?.organization_name ||
      row?.org_name ||
      row?.organizationName ||
      organizationObject?.name ||
      organizationObject?.org_name ||
      (typeof row?.organization === 'string' ? row.organization : '') ||
      '';
    const scheduleIdentity =
      [row?.uu_id, row?.scan_id, row?.id].find((value) => isUsableScanIdentity(value)) || null;
    return {
      ...row,
      recordKind: 'schedule',
      scan_id: scheduleIdentity,
      scan_name: row?.scan_name || '',
      target: row?.target || '',
      scan_status: row?.scan_status || 'Scheduled',
      next_run_time: row?.next_run_time || '',
      start_time: row?.start_time || '',
      date_time: row?.next_run_time || row?.start_time || '',
      scanner_deployment: inferScheduledNetworkDeployment(row),
      scan_freqency: row?.scan_freqency || row?.scan_frequency || row?.recurrence || '',
      organization_name: organizationName,
      org_name: organizationName,
      site_name: siteName,
      site_uuid: row?.site_uuid || row?.site_uu_id || siteObject?.uu_id || '',
    };
  });
}

export function mapScheduledWebRowsToHistoryLikeRows(scheduleRows) {
  return (Array.isArray(scheduleRows) ? scheduleRows : []).map((row) => {
    const siteObject = row?.site && typeof row.site === 'object' ? row.site : null;
    return {
      ...row,
      recordKind: 'schedule',
      scan_id: row?.uu_id || row?.scan_id || null,
      scan_name: row?.scan_name || '',
      target: row?.url || row?.target || '',
      status: row?.status || 'Scheduled',
      next_run_time: row?.next_run_time || '',
      start_time: row?.start_time || '',
      scanDate: row?.next_run_time || row?.start_time || '',
      scan_freqency: row?.scan_freqency || row?.scan_frequency || row?.recurrence || '',
      organization_name: row?.organization_name || row?.org_name || row?.organization || '',
      site_name: row?.site_name || row?.site || '',
      site_uuid: row?.site_uuid || row?.site_uu_id || siteObject?.uu_id || '',
      scanner_id: row?.scanner_id || row?.setting_id || row?.web_scanner_uuid || null,
      progressPercent: null,
    };
  });
}

// The schedule APIs send a canonical frequency; the Recurrence column shows its label.
const SCHEDULE_FREQUENCY_LABELS = {
  'one-off': 'One-off',
  daily: 'Daily',
  weekly: 'Weekly',
  fortnightly: 'Fortnightly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
};

export const normalizeScansRow = (raw) => {
  const source =
    (raw?.serializer && typeof raw.serializer === 'object' && raw.serializer) ||
    (raw?.data && typeof raw.data === 'object' && raw.data) ||
    (raw?.result && typeof raw.result === 'object' && raw.result) ||
    (raw?.scan && typeof raw.scan === 'object' && raw.scan) ||
    raw ||
    {};

  const pickText = (...values) => {
    for (const v of values) {
      if (v == null) continue;
      if (typeof v === 'string') {
        const s = v.trim();
        if (s.toLowerCase() === 'null' || s.toLowerCase() === 'undefined') continue;
        if (s) return s;
        continue;
      }
      if (typeof v === 'number') return String(v);
      if (typeof v === 'object') {
        const s =
          v.text ??
          v.label ??
          v.value ??
          v.name ??
          v.url ??
          v.target ??
          v.endpoint ??
          '';
        const t = String(s || '').trim();
        if (t) return t;
      }
    }
    return '';
  };

  const flattenStringValues = (obj, maxDepth = 4, depth = 0, out = []) => {
    if (obj == null || depth > maxDepth) return out;
    if (typeof obj === 'string') {
      const s = obj.trim();
      if (s) out.push(s);
      return out;
    }
    if (typeof obj === 'number' || typeof obj === 'boolean') {
      out.push(String(obj));
      return out;
    }
    if (Array.isArray(obj)) {
      obj.forEach((v) => flattenStringValues(v, maxDepth, depth + 1, out));
      return out;
    }
    if (typeof obj === 'object') {
      Object.values(obj).forEach((v) => flattenStringValues(v, maxDepth, depth + 1, out));
    }
    return out;
  };

  const looksLikeUrlOrHost = (value) => {
    const s = String(value || '').trim();
    if (!s) return false;
    if (/^https?:\/\//i.test(s)) return true;
    if (/^[a-z0-9.-]+\.[a-z]{2,}(\/.*)?$/i.test(s)) return true;
    if (/^\d{1,3}(\.\d{1,3}){3}(\/\d{1,2})?$/i.test(s)) return true;
    return false;
  };

  const looksLikeDateTime = (value) => {
    const s = String(value || '').trim();
    if (!s) return false;
    if (
      /^\d{4}-\d{2}-\d{2}[tT ]\d{2}:\d{2}/.test(s) ||
      /^[A-Za-z]{3},\s\d{1,2}\s[A-Za-z]{3}\s\d{4}\s\d{2}:\d{2}/.test(s) ||
      /^\d{4}-\d{2}-\d{2}$/.test(s)
    ) {
      const d = new Date(s);
      return !Number.isNaN(d.getTime());
    }
    return false;
  };

  const deepStrings = flattenStringValues(source, 4);
  const deepTargetFallback = deepStrings.find(looksLikeUrlOrHost) || '';
  const deepDateFallback = deepStrings.find(looksLikeDateTime) || '';
  const fallbackScanId = pickText(source.scanId, source.scan_id, source.uu_id, source.id);
  const organizationObject =
    (source.organization && typeof source.organization === 'object' && source.organization) ||
    (source.org && typeof source.org === 'object' && source.org) ||
    (source.site && typeof source.site === 'object' && source.site.organization) ||
    null;
  const siteObject = source.site && typeof source.site === 'object' ? source.site : null;
  const orgName = pickText(
    source.organization_name,
    source.organizationName,
    source.org_name,
    source.orgName,
    organizationObject?.name,
    organizationObject?.org_name,
    source.organization,
    source.org,
    source.company_name,
    source.customer_name
  );
  const siteName = pickText(
    source.site_name,
    source.siteName,
    siteObject?.site_name,
    siteObject?.name,
    typeof source.site === 'string' && !/^[0-9a-f-]{36}$/i.test(source.site.trim()) ? source.site : ''
  );
  const scanName = pickText(source.scan_name, source.scanName, source.vanity_name, source.vanityName);

  let statusText =
    typeof source.status === 'object' && source.status !== null
      ? source.status.text ?? source.status.label ?? source.status.value ?? ''
      : source.status;
  if (!String(statusText ?? '').trim()) {
    statusText =
      source.scan_status ??
      source.scanStatus ??
      source.state ??
      source.scan_state ??
      source.lifecycle ??
      '';
  }
  const progressPercent = extractScanProgressPercent(
    source.progressPercent,
    source.progress_percent,
    source.scan_progress,
    source.progress,
    source.percent_complete,
    source.percentage,
    source.completion,
    source.status,
    source.scan_status,
  );
  const totals = source.totals && typeof source.totals === 'object' ? source.totals : {};
  const rb = source.risk_breakdown ?? source.risks_breakdown ?? source.risk_counts ?? source.severity_counts ?? {};
  const firstDefined = (...values) => values.find((v) => v !== undefined && v !== null && v !== '');
  const hasSeverityPayload = firstDefined(
    rb.critical, totals.critical, source.critical, source.critical_count, source.critical_vul, source.criticalVul,
    rb.high, totals.high, source.high, source.high_count, source.high_vul, source.highVul,
    rb.medium, totals.medium, source.medium, source.medium_count, source.medium_vul, source.mediumVul,
    rb.low, totals.low, source.low, source.low_count, source.low_vul, source.lowVul,
    source.riskCritical, source.riskHigh, source.riskMedium, source.riskLow,
    totals.total, source.risks, source.total_vul
  ) !== undefined;
  const riskCriticalOpen = numOr0(
    rb.critical ?? totals.critical ?? source.critical ?? source.critical_count ?? source.critical_vul ?? source.criticalVul ?? source.riskCritical
  );
  const riskHighOpen = numOr0(rb.high ?? totals.high ?? source.high ?? source.high_count ?? source.high_vul ?? source.highVul ?? source.riskHigh);
  const riskMediumOpen = numOr0(
    rb.medium ?? totals.medium ?? source.medium ?? source.medium_count ?? source.medium_vul ?? source.mediumVul ?? source.riskMedium
  );
  const riskLowOpen = numOr0(rb.low ?? totals.low ?? source.low ?? source.low_count ?? source.low_vul ?? source.lowVul ?? source.riskLow);
  const riskCriticalDup = numOr0(
    rb.dup_critical ?? totals.dup_critical ?? source.dup_critical ?? source.duplicate_critical ?? source.dupCritical
  );
  const riskHighDup = numOr0(rb.dup_high ?? totals.dup_high ?? source.dup_high ?? source.duplicate_high ?? source.dupHigh);
  const riskMediumDup = numOr0(
    rb.dup_medium ?? totals.dup_medium ?? source.dup_medium ?? source.duplicate_medium ?? source.dupMedium
  );
  const riskLowDup = numOr0(rb.dup_low ?? totals.dup_low ?? source.dup_low ?? source.duplicate_low ?? source.dupLow);
  // Keep open counts authoritative, but surface duplicate severities when open is zero.
  const riskCritical = riskCriticalOpen > 0 ? riskCriticalOpen : riskCriticalDup;
  const riskHigh = riskHighOpen > 0 ? riskHighOpen : riskHighDup;
  const riskMedium = riskMediumOpen > 0 ? riskMediumOpen : riskMediumDup;
  const riskLow = riskLowOpen > 0 ? riskLowOpen : riskLowDup;
  const fromParts = riskCritical + riskHigh + riskMedium + riskLow;
  const totalFallback = numOr0(
    totals.total ?? source.risks ?? source.risk_count ?? source.total_risks ?? source.total_vul ?? source.totalVul
  );
  const recordKind = String(source.recordKind || source.record_kind || '').toLowerCase() === 'schedule'
    ? 'schedule'
    : 'run';
  const scheduled = recordKind === 'schedule' ||
    getScansLifecycleKind(statusText, progressPercent, source.task_lifecycle ?? source.taskLifecycle) === 'scheduled';
  const nextRunAt = pickText(source.next_run_time, source.nextRunTime, source.next_run, source.nextRunAt);
  const scheduledAt = pickText(nextRunAt, source.scheduled_time, recordKind === 'schedule' ? source.start_time : '');
  const targetValue = pickText(
    source.target,
    source.scan_url,
    source.scanUrl,
    source.target_url,
    source.targetUrl,
    source.url,
    source.hostname,
    source.endpoint,
    source.ip,
    source.cidr,
    source.scan_target,
    source.scanTarget,
    deepTargetFallback
  );
  return {
    recordKind,
    scanName: scanName || (targetValue ? '' : pickText(source.name, source.endpoint)),
    target: targetValue || scanName || (fallbackScanId ? `scan-${String(fallbackScanId).slice(0, 8)}` : 'unknown-target'),
    orgName,
    siteName,
    networkType: normalizeNetworkType({
      ...source,
      scanner_deployment:
        source.scanner_deployment ??
        source.scannerDeployment ??
        source.deployment_type ??
        source.deploymentType,
    }),
    status: String(statusText ?? source.scan_status ?? source.scanStatus ?? '').trim(),
    scanDate: scheduled ? scheduledAt : pickText(
      source.scanDate,
      source.scan_date,
      source.last_scan,
      source.completed_at,
      source.date_time,
      source.dateTime,
      source.scan_date_time,
      source.date,
      source.created_at,
      source.updated_at,
      source.date_time_created,
      source.start_scan_date,
      source.created,
      source.modified,
      source.timestamp,
      source.ts,
      source.start_time,
      deepDateFallback
    ),
    riskCritical: hasSeverityPayload ? riskCritical : null,
    riskHigh: hasSeverityPayload ? riskHigh : null,
    riskMedium: hasSeverityPayload ? riskMedium : null,
    riskLow: hasSeverityPayload ? riskLow : null,
    risks: hasSeverityPayload ? (fromParts > 0 ? fromParts : totalFallback) : null,
    risksAvailable: hasSeverityPayload,
    progressPercent,
    startedAt: scheduled ? '' : pickText(
      source.started_at,
      source.startedAt,
      source.start_scan_date,
      source.start_time,
      source.date_time,
      source.dateTime
    ),
    completedAt: (() => {
      const explicit = pickText(
        source.completed_at,
        source.completedAt,
        source.terminal_state_at,
        source.terminalStateAt,
        source.end_time,
        source.finished_at,
        source.finishedAt
      );
      if (explicit) return explicit;
      const statusForLifecycle = String(statusText ?? source.scan_status ?? source.scanStatus ?? '').trim();
      const lifecycle = getScansLifecycleKind(
        statusForLifecycle,
        progressPercent,
        source.task_lifecycle ?? source.taskLifecycle ?? null
      );
      if (lifecycle === 'completed' || lifecycle === 'failed') {
        return pickText(
          source.date_time,
          source.dateTime,
          source.scan_date,
          source.scanDate,
          source.last_scan,
          source.updated_at,
          source.modified,
          source.timestamp,
          source.ts,
          deepDateFallback
        );
      }
      return '';
    })(),
    nextRunAt: nextRunAt || (scheduled ? scheduledAt : ''),
    recurrence: (() => {
      const value = pickText(source.scan_freqency, source.scan_frequency, source.recurrence, source.frequency, source.scan_schedule);
      return SCHEDULE_FREQUENCY_LABELS[String(value).toLowerCase()] || value;
    })(),
    errorDetail: pickText(
      source.task_status_reason,
      source.taskStatusReason,
      source.error,
      source.error_detail,
      source.errorDetail,
      source.failure_reason,
      source.failureReason,
      source.message
    ),
    scanId: (
      [source.scanId, source.scan_id, source.uu_id, source.id].find((value) => isUsableScanIdentity(value))
      || null
    ),
    siteUuid:
      pickText(
        source.site_uuid,
        source.siteUuid,
        source.site_uu_id,
        siteObject?.uu_id,
        siteObject?.site_uuid
      ) || null,
    scannerId:
      source.scannerId
      ?? source.scanner_id
      ?? source.web_scanner_uuid
      ?? source.network_scanner_uuid
      ?? source.setting_id
      ?? source.scanner_zap?.setting_id
      ?? source.scanner_openvas?.setting_id
      ?? null,
    scanType:
      String(source.scanType || source.scan_type || '').trim().toLowerCase() === 'network'
        ? 'network'
        : String(source.scanType || source.scan_type || '').trim().toLowerCase() === 'web'
          ? 'web'
          : source.scanType || source.scan_type || null,
    ip: pickText(source.ip, source.endpoint, source.target, source.scan_url),
    taskLifecycle: source.task_lifecycle ?? source.taskLifecycle ?? null,
    stage: String(source.task_status_reason ?? source.taskStatusReason ?? '').trim(),
  };
};

export const getNormalizedScanIdentity = (raw) => {
  const normalized = normalizeScansRow(raw);
  const kind = isScanScheduleRecord(normalized) ? 'schedule' : 'run';
  const scanId = String(normalized.scanId || '').trim();
  if (isUsableScanIdentity(scanId)) return `${kind}:scan:${scanId}`;

  const target = String(normalized.target || '').trim().toLowerCase();
  const scanDate = String(normalized.scanDate || '').trim();
  if (target && scanDate) return `${kind}:target:${target}|date:${scanDate}`;
  if (target) return `${kind}:target:${target}`;
  return '';
};

export function isScanScheduleRecord(raw) {
  const normalized = raw && typeof raw === 'object' && raw.recordKind
    ? raw
    : normalizeScansRow(raw);
  if (normalized.recordKind === 'schedule') return true;
  return getScansLifecycleKind(normalized.status, normalized.progressPercent, normalized.taskLifecycle) === 'scheduled';
}

/** Run rows that are still active or need attention (in progress + failed). */
export function isScanInProgressOrFailedRun(raw) {
  const normalized = raw && typeof raw === 'object' && raw.recordKind
    ? raw
    : normalizeScansRow(raw);
  if (isScanScheduleRecord(normalized)) return false;
  const kind = getScansLifecycleKind(
    normalized.status,
    normalized.progressPercent,
    normalized.taskLifecycle
  );
  return kind === 'in_progress' || kind === 'failed';
}

export function scansSearchHaystack(row) {
  return [
    row.scanName,
    row.target,
    row.orgName,
    row.siteName,
    row.networkType,
    row.status,
    scansStatusSearchText(row.status),
    row.recurrence,
    row.errorDetail,
    String(row.scanType || ''),
  ]
    .map((value) => String(value || '').toLowerCase())
    .join(' ');
}

export function formatScanDateTime(value, timeZone) {
  if (!value) return { display: '—', date: '—', time: '', timeZoneLabel: '', title: '' };
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    const raw = String(value);
    return { display: raw, date: raw, time: '', timeZoneLabel: '', title: raw };
  }
  const tz = String(timeZone || '').trim();
  const baseOpts = {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };
  try {
    const opts = tz ? { ...baseOpts, timeZone: tz } : baseOpts;
    const parts = new Intl.DateTimeFormat('en-GB', opts).formatToParts(d);
    const get = (type) => parts.find((part) => part.type === type)?.value || '';
    const date = [get('day'), get('month'), get('year')].filter(Boolean).join(' ');
    const dayPeriod = get('dayPeriod');
    const timeCore = [get('hour'), get('minute')].filter(Boolean).join(':');
    const time = [timeCore, dayPeriod].filter(Boolean).join(' ');
    let timeZoneLabel = '';
    if (tz) {
      const tzParts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' }).formatToParts(d);
      timeZoneLabel = tzParts.find((part) => part.type === 'timeZoneName')?.value || tz;
    } else {
      const tzParts = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).formatToParts(d);
      timeZoneLabel = tzParts.find((part) => part.type === 'timeZoneName')?.value || '';
    }
    const display = [date, time].filter(Boolean).join(' ');
    return {
      display,
      date: date || display,
      time,
      timeZoneLabel,
      title: `${display} ${timeZoneLabel}`.trim(),
    };
  } catch (_) {
    const iso = d.toISOString();
    return { display: iso, date: iso, time: '', timeZoneLabel: 'UTC', title: iso };
  }
}

export function networkLocationKey(row) {
  const label = String(row?.networkType || normalizeNetworkType(row) || '').toLowerCase();
  if (label.includes('perimeter')) return 'perimeter';
  if (label.includes('on-premise') || label.includes('on premise') || label.includes('onprem')) return 'onprem';
  return '';
}

export const upsertScanRowByIdentity = (existingRows, incomingRow) => {
  if (!Array.isArray(existingRows)) return existingRows;
  if (!incomingRow || typeof incomingRow !== 'object') return existingRows;

  const incomingIdentity = getNormalizedScanIdentity(incomingRow);
  if (!incomingIdentity) {
    return [incomingRow, ...existingRows];
  }

  const rows = [...existingRows];
  const existingIndex = rows.findIndex((row) => getNormalizedScanIdentity(row) === incomingIdentity);
  if (existingIndex >= 0) {
    rows[existingIndex] = {
      ...rows[existingIndex],
      ...incomingRow,
    };
    return rows;
  }

  rows.unshift(incomingRow);
  return rows;
};

export const dedupeScanRowsByIdentity = (rows) => {
  if (!Array.isArray(rows)) return [];
  const deduped = [];
  const indices = new Map();

  rows.forEach((row) => {
    const identity = getNormalizedScanIdentity(row);
    if (identity && indices.has(identity)) {
      const index = indices.get(identity);
      const existing = normalizeScansRow(deduped[index]);
      const incoming = normalizeScansRow(row);
      if (identity.startsWith('schedule:')) {
        const preferIncoming = incoming.recordKind === 'schedule' && existing.recordKind !== 'schedule';
        const preferred = preferIncoming ? incoming : existing;
        const fallback = preferIncoming ? existing : incoming;
        deduped[index] = {
          ...(preferIncoming ? row : deduped[index]),
          orgName: preferred.orgName || fallback.orgName,
          siteName: preferred.siteName || fallback.siteName,
          scanName: preferred.scanName || fallback.scanName,
        };
      }
      return;
    }
    if (identity) indices.set(identity, deduped.length);
    deduped.push(row);
  });

  return deduped;
};

/** task_status_reason of a completed network scan in which nothing was reachable (backend networkscanners/engine_canary.py). */
export const NO_LIVE_HOSTS_NOTE = 'No live hosts found';

/** Map backend scan status text → lifecycle kind used by pills, progress bars, and filters. */
export const getScansLifecycleKind = (status, progressPercent = null, taskLifecycle = null) => {
  if (taskLifecycle) {
    const lc = String(taskLifecycle).toLowerCase().trim();
    if (lc === 'completed') return 'completed';
    if (lc === 'recovering') return 'recovering';
    if (lc === 'interrupted' || lc === 'failed' || lc === 'aborted' || lc === 'cancelled' || lc === 'stopped') return 'failed';
    if (lc === 'scheduled') return 'scheduled';
    if (lc === 'queued' || lc === 'starting' || lc === 'running' || lc === 'in_progress' || lc === 'launching') return 'in_progress';
  }

  const s = String(status || '').toLowerCase().trim();
  if (s === 'scheduled' || s.includes('next run')) return 'scheduled';

  const numeric = clampProgressPercent(progressPercent ?? status);
  if (numeric !== null) {
    return numeric >= 100 ? 'completed' : 'in_progress';
  }

  if (!s) return 'unknown';
  if (s.includes('recover')) return 'recovering';
  if (
    s.includes('fail') ||
    s.includes('error') ||
    s.includes('abort') ||
    s.includes('stop') ||
    s.includes('cancel') ||
    s.includes('interrupt')
  ) {
    return 'failed';
  }
  if (s.includes('incomplete')) return 'unknown';
  if (s.includes('schedul')) {
    return 'scheduled';
  }
  if (s.includes('complete') || s.includes('success') || s.includes('done')) return 'completed';
  if (
    s.includes('run') ||
    s.includes('progress') ||
    s.includes('active') ||
    s.includes('start') ||
    s.includes('launch') ||
    s.includes('queue') ||
    s.includes('pend')
  ) {
    return 'in_progress';
  }
  return 'unknown';
};

export const getScansStatusKind = (status, progressPercent = null, taskLifecycle = null) =>
  getScansLifecycleKind(status, progressPercent, taskLifecycle);

/** Completed (and unknown) scans may show risk counts; these lifecycle states do not. */
export const scansLifecycleHidesRisks = (status, progressPercent = null, taskLifecycle = null) => {
  const k = getScansLifecycleKind(status, progressPercent, taskLifecycle);
  return k === 'in_progress' || k === 'failed' || k === 'scheduled' || k === 'recovering';
};

/** Extra tokens for client-side search so queries match pill labels (e.g. "in progress" vs API "Running"). */
export const scansStatusSearchText = (status) => {
  const raw = String(status ?? '').trim().toLowerCase();
  const kind = getScansStatusKind(status);
  if (kind === 'completed') {
    return `${raw} completed complete done success`;
  }
  if (kind === 'in_progress') {
    return `${raw} in progress progress running scheduled pending queued active started`;
  }
  if (kind === 'failed') {
    return `${raw} failed failure error abort aborted`;
  }
  return raw;
};

// --- Date/time picker helpers ---

export function toDatetimeLocalValue(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function toDateInputValue(date) {
  return toDatetimeLocalValue(date).slice(0, 10);
}

export function toTimeInputValue(date) {
  return toDatetimeLocalValue(date).slice(11, 16);
}

export const padTimeUnit = (n) => String(n).padStart(2, '0');

/** Normalize "HH:mm" (and optional :ss) to a valid 24h clock string for select value + API. */
export function normalizeTimeString(hhmm) {
  const raw = String(hhmm || '').trim();
  const parts = raw.split(':').map((p) => p.trim());
  let h = parseInt(parts[0], 10);
  let m = parseInt(parts[1], 10);
  if (!Number.isFinite(h)) h = 0;
  if (!Number.isFinite(m)) m = 0;
  h = ((h % 24) + 24) % 24;
  m = Math.max(0, Math.min(59, m));
  return `${padTimeUnit(h)}:${padTimeUnit(m)}`;
}

export const TIME_PICKER_HOURS_12 = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
export const TIME_PICKER_MINUTES = Array.from({ length: 60 }, (_, i) => i);
export const TIME_PICKER_PERIODS = ['AM', 'PM'];

export function time24To12Parts(h24, minute) {
  const period = h24 >= 12 ? 'PM' : 'AM';
  let h12 = h24 % 12;
  if (h12 === 0) h12 = 12;
  return { h12, minute, period };
}

export function hour12PeriodTo24(h12, period) {
  if (period === 'AM') {
    return h12 === 12 ? 0 : h12;
  }
  return h12 === 12 ? 12 : h12 + 12;
}

export function formatTimePickerDisplay(hhmm) {
  const t = normalizeTimeString(hhmm);
  const [h, m] = t.split(':').map((x) => parseInt(x, 10));
  const d = new Date(2000, 0, 1, h, m, 0, 0);
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export function daysInMonth(year, month1to12) {
  return new Date(year, month1to12, 0).getDate();
}

/** Valid YYYY-MM-DD for API; invalid input falls back to today. */
export function normalizeDateString(raw) {
  const s = String(raw || '').trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return toDateInputValue(new Date());
  let y = parseInt(m[1], 10);
  let mo = parseInt(m[2], 10);
  let d = parseInt(m[3], 10);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return toDateInputValue(new Date());
  y = Math.max(1900, Math.min(2100, y));
  mo = Math.max(1, Math.min(12, mo));
  const dim = daysInMonth(y, mo);
  d = Math.max(1, Math.min(dim, d));
  return `${y}-${padTimeUnit(mo)}-${padTimeUnit(d)}`;
}

export function formatDatePickerDisplay(iso) {
  const t = normalizeDateString(iso);
  const parts = t.split('-');
  const y = parseInt(parts[0], 10);
  const mo = parseInt(parts[1], 10);
  const d = parseInt(parts[2], 10);
  const dt = new Date(y, mo - 1, d);
  if (Number.isNaN(dt.getTime())) return t;
  return dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function getMonthShortLabel(month1to12) {
  return new Date(2000, month1to12 - 1, 1).toLocaleString(undefined, { month: 'short' });
}

export function buildDatePickerYears() {
  const c = new Date().getFullYear();
  const years = [];
  for (let y = c - 5; y <= c + 15; y += 1) years.push(y);
  return years;
}

export const DATE_PICKER_MONTH_NUMBERS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

// --- Timezone helpers ---

/**
 * Full IANA list when `Intl.supportedValuesOf('timeZone')` exists; otherwise a short fallback.
 * Ensures the user's current zone stays in the list even if the API omits it.
 */
export function getTimezoneOptions(currentTz) {
  const fallback = [
    'UTC',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Berlin',
    'Asia/Dubai',
    'Asia/Kolkata',
    'Asia/Singapore',
    'Asia/Tokyo',
    'Australia/Sydney'
  ];
  let values = fallback;
  try {
    if (typeof Intl !== 'undefined' && typeof Intl.supportedValuesOf === 'function') {
      const tzs = Intl.supportedValuesOf('timeZone');
      if (Array.isArray(tzs) && tzs.length > 0) values = tzs;
    }
  } catch (_) {
    // Keep fallback list when browser cannot provide full timezone list.
  }
  const set = new Set(values);
  if (currentTz) set.add(currentTz);
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

export function getPreferredTimezoneDefault() {
  return 'Australia/Melbourne';
}

export function getNetworkScanScheduleDefaults(userTimezone = getPreferredTimezoneDefault(), now = new Date()) {
  const tz = String(userTimezone || getPreferredTimezoneDefault());
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(now);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return {
    startDate: `${values.year}-${values.month}-${values.day}`,
    startTime: `${values.hour}:${values.minute}`,
    timezone: tz
  };
}

// --- Validation helpers ---

export function splitScanInputEntries(raw) {
  return String(raw || '')
    .split(/[\n,]+/)
    .map((value) => value.trim())
    .filter(Boolean);
}

export function isValidIpv4Address(value) {
  const parts = String(value || '').trim().split('.');
  if (parts.length !== 4) return false;
  return parts.every((part) => {
    if (!/^\d+$/.test(part)) return false;
    if (part.length > 1 && part.startsWith('0')) return false;
    const n = Number(part);
    return Number.isInteger(n) && n >= 0 && n <= 255;
  });
}

export function ipv4ToInt(value) {
  if (!isValidIpv4Address(value)) return null;
  return value
    .split('.')
    .map((part) => Number(part))
    .reduce((acc, octet) => acc * 256 + octet, 0);
}

export function isValidIpv4Cidr(value) {
  const raw = String(value || '').trim();
  const parts = raw.split('/');
  if (parts.length !== 2) return false;
  const [ip, maskRaw] = parts;
  if (!isValidIpv4Address(ip)) return false;
  if (!/^\d+$/.test(maskRaw)) return false;
  const mask = Number(maskRaw);
  return Number.isInteger(mask) && mask >= 24 && mask <= 32;
}

export function isValidIpv4Range(value) {
  const raw = String(value || '').trim();
  const parts = raw.split('-').map((x) => x.trim());
  if (parts.length !== 2) return false;
  const [start, end] = parts;
  if (!isValidIpv4Address(start) || !isValidIpv4Address(end)) return false;

  const startParts = start.split('.');
  const endParts = end.split('.');
  const same24 =
    startParts[0] === endParts[0] &&
    startParts[1] === endParts[1] &&
    startParts[2] === endParts[2];
  if (!same24) return false;

  const startInt = ipv4ToInt(start);
  const endInt = ipv4ToInt(end);
  return startInt !== null && endInt !== null && startInt <= endInt;
}

export function isValidFqdn(value) {
  const raw = String(value || '').trim();
  if (!raw || raw.length > 253) return false;
  const fqdnRegex = /^(?!-)[A-Za-z0-9-]{1,63}(?<!-)(\.[A-Za-z0-9-]{1,63})*\.[A-Za-z]{2,}$/;
  return fqdnRegex.test(raw);
}

export function validateNetworkTargetsInput(raw) {
  const entries = splitScanInputEntries(raw);
  if (entries.length === 0) {
    return { valid: false, message: 'Please provide at least one network target.' };
  }

  const invalid = entries.find(
    (entry) => !isValidIpv4Address(entry) && !isValidIpv4Cidr(entry) && !isValidIpv4Range(entry) && !isValidFqdn(entry)
  );

  if (invalid) {
    return {
      valid: false,
      message:
        'Network targets must be an IPv4 address, CIDR (/24 to /32), IPv4 range in the same /24, or FQDN (e.g. www.example.com).',
      invalidEntry: invalid,
    };
  }

  return { valid: true, sanitized: entries.join(', ') };
}

export function isValidHttpHttpsUrl(value) {
  try {
    const parsed = new URL(String(value || '').trim());
    if (!['http:', 'https:'].includes(parsed.protocol)) return false;
    return Boolean(parsed.hostname);
  } catch {
    return false;
  }
}

export function validateWebUrlsInput(raw) {
  const entries = splitScanInputEntries(raw);
  if (entries.length === 0) {
    return { valid: false, message: 'Please provide at least one URL.' };
  }

  const invalid = entries.find((entry) => !isValidHttpHttpsUrl(entry));
  if (invalid) {
    return {
      valid: false,
      message: 'Each URL must start with http:// or https:// (example: https://www.test.com).',
      invalidEntry: invalid,
    };
  }

  return { valid: true, sanitized: entries.join('\n') };
}

const PORT_LIST_MAX_LEN = 500;
const SCHEDULE_NAME_MAX_LEN = 120;

/** Optional TCP/UDP port list: empty OK; else comma/space-separated ports or ranges 1–65535. */
export function validatePortListInput(raw, { label = 'Ports' } = {}) {
  const text = String(raw ?? '').replace(/\0/g, '').trim();
  if (!text) return { valid: true, sanitized: '' };
  if (text.length > PORT_LIST_MAX_LEN) {
    return { valid: false, message: `${label} list is too long (max ${PORT_LIST_MAX_LEN} characters).` };
  }
  const parts = text.split(/[,\s]+/).map((p) => p.trim()).filter(Boolean);
  const portOk = (n) => Number.isInteger(n) && n >= 1 && n <= 65535;
  for (const part of parts) {
    if (/^\d+$/.test(part)) {
      if (!portOk(Number(part))) {
        return { valid: false, message: `${label} must be between 1 and 65535.`, invalidEntry: part };
      }
      continue;
    }
    const range = part.match(/^(\d+)\s*-\s*(\d+)$/);
    if (range) {
      const a = Number(range[1]);
      const b = Number(range[2]);
      if (!portOk(a) || !portOk(b) || a > b) {
        return {
          valid: false,
          message: `${label} ranges must be low-high within 1–65535 (e.g. 80-443).`,
          invalidEntry: part,
        };
      }
      continue;
    }
    return {
      valid: false,
      message: `${label} must be a port or range (e.g. 80, 443, 8000-8080).`,
      invalidEntry: part,
    };
  }
  return { valid: true, sanitized: parts.join(', ') };
}

export function sanitizeScheduleName(raw) {
  return String(raw ?? '')
    .replace(/\0/g, '')
    .replace(/[\u0000-\u001F\u007F]/g, '')
    .trim()
    .slice(0, SCHEDULE_NAME_MAX_LEN);
}

export function validateScheduleNameInput(raw, { required = false } = {}) {
  const sanitized = sanitizeScheduleName(raw);
  if (!sanitized) {
    if (required) {
      return { valid: false, message: 'Please enter a schedule name for recurring scans.', sanitized: '' };
    }
    return { valid: true, sanitized: '' };
  }
  return { valid: true, sanitized };
}

